import { FC, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  ActiveCase,
  setActiveCase,
  loadFirstActiveCaseRecordFromResponse
} from 'reducers/activecase/activecaseSlice'
import {
  GET_CASE_LEVEL_DASHBOARD,
  GetCaseLevelDashboardResponse
} from 'share/queries/cases'
import { AccountCreationStep } from 'share/utils/utils'
import { logOut } from 'share/utils/authenticationUtils'
import AiDocuments from 'components/features/dashboard/document/AiDocuments'
import ChatBot from 'components/organisms/ChatBot'
import CaseOverview from 'components/features/dashboard/CaseOverview'

interface ParamTypes {
  caseId?: string
}

const CaseLevelDashboard: FC = () => {
  const history = useHistory()
  const authentication = useAppSelector((state) => state.authentication)
  const activeCase = useAppSelector((state) => state.activeCase)
  const dispatch = useAppDispatch()
  const { caseId } = useParams<ParamTypes>()
  const [showChatBot, setShowChatBot] = useState(false)
  const [showAiDocs, setShowAiDocs] = useState(false)
  const [showCaseOverview, setShowCaseOverview] = useState(false)
  const [loadingDelay, setLoadingDelay] = useState(false)

  const [getCaseDashboard, { loading, data, error }] =
    useLazyQuery<GetCaseLevelDashboardResponse>(GET_CASE_LEVEL_DASHBOARD, {
      fetchPolicy: 'network-only'
    })

  useEffect(() => {
    if (authentication) {
      if (authentication.account) {
        if (
          authentication.account.accountCreationStep !=
          AccountCreationStep.CompletedAllSteps
        ) {
          logOut(dispatch)
        }
      }
    }
  }, [authentication])

  useEffect(() => {
    let localCaseId = 0
    if (caseId) {
      localCaseId = parseInt(caseId)
    }

    getCaseDashboard({ variables: { caseId: localCaseId } })
  }, [caseId])

  useEffect(() => {
    loading ? setLoadingDelay(true) : setLoadingDelay(false)
    let newActiveCase: ActiveCase | null = null
    if (!loading && data && !error) {
      newActiveCase = loadFirstActiveCaseRecordFromResponse(data)
      if (newActiveCase) {
        dispatch(setActiveCase(newActiveCase))
      }
    }
  }, [loading, data, error])

  // go to client dashboard on error
  if (error) {
    history.push('/client')
  }

  useEffect(() => {
    if (activeCase?.matter) {
      if (activeCase.matter.isAiEnabled && activeCase.matter.isChatBotEnabled) {
        setShowChatBot(true)
      } else if (activeCase.matter.isAiEnabled) {
        setShowAiDocs(true)
      } else {
        setShowCaseOverview(true)
      }
    }
  }, [activeCase])

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      {showChatBot && <ChatBot />}
      {showAiDocs && <AiDocuments />}
      {showCaseOverview && <CaseOverview />}
    </>
  )
}

export default CaseLevelDashboard
